import MoneyOff from "@mui/icons-material/MoneyOff";
import NewReleases from "@mui/icons-material/NewReleases";
import Savings from "@mui/icons-material/Savings";
import Security from "@mui/icons-material/Security";
import Settings from "@mui/icons-material/Settings";
import Storage from "@mui/icons-material/Storage";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ReactElement } from "react";
import pageData from "../../data/pages/solutions-intelligent-trace-sampling.json";
import { AccordionSection } from "../../components/AccordionSection/AccordionSection";
import { CardsSection } from "../../components/CardsSection";
import type { MdaiCardProps } from "../../components/MdaiCard";
import { PageTemplate } from "../../components/PageTemplate";
import { TabCard } from "../../components/Tabs/TabCard";
import type { TabContentProps } from "../../components/Tabs/TabsSection";
import { TabsSection } from "../../components/Tabs/TabsSection";
import { TextGroup } from "../../components/TextGroup";
import type { SolutionDetailPageTemplate } from "../../types/solutionDetailPageTemplate";
import { resolveCdnAssetUrl } from "../../utils/cdnAssetUrl";

const typedPageData = pageData as SolutionDetailPageTemplate;
const PRIMARY_CARD_ICONS = [Savings, Security, Settings, Storage] as const;
const BENEFIT_CARD_ICONS = [Savings, Security, NewReleases, MoneyOff] as const;

const ITSCards1: MdaiCardProps[] = (typedPageData.cards ?? [])
  .slice(0, 4)
  .map((card, index) => ({
    Icon: PRIMARY_CARD_ICONS[index] ?? Savings,
    title: card.title,
    body: card.body,
  }));

const ITSTabs: TabContentProps[] = (typedPageData.tabsSection?.tabs ?? []).map((tab, idx) => ({
  type: "component" as const,
  title: tab.title,
  content: (
    <TabCard
      id={`intelligent-ts-tc-${String(idx + 1)}`}
      title={tab.cardTitle}
      subtitle={tab.cardSubtitle}
      bodies={tab.bodies}
      bulletHeading={tab.bulletHeading}
      bullets={tab.bullets}
    />
  ),
}));

const ITSCards2: MdaiCardProps[] = (typedPageData.cards ?? [])
  .slice(4)
  .map((card, index) => ({
    Icon: BENEFIT_CARD_ICONS[index] ?? Savings,
    title: card.title,
    body: card.body,
  }));

const primarySection = typedPageData.cardsSections?.find((s) => s.id === "primary");
const benefitsSection = typedPageData.cardsSections?.find((s) => s.id === "benefits");

export default function IntelligentTraceSampling(): ReactElement {
  return (
    <Box>
      <PageTemplate
        id="intelligent-ts"
        hero={{
          title: typedPageData.hero.title,
          subtitle: typedPageData.hero.subtitle,
          image: typedPageData.hero.image
            ? {
                src: resolveCdnAssetUrl(typedPageData.hero.image.src),
                alt: typedPageData.hero.image.alt,
              }
            : undefined,
          buttons: typedPageData.hero.button
            ? [{ text: typedPageData.hero.button.text, href: typedPageData.hero.button.href }]
            : [],
        }}
        topTabs={{
          headline: typedPageData.topTabs.headline,
          tabs: (typedPageData.topTabs.tabs ?? []).map((tab) => ({
            src: resolveCdnAssetUrl(tab.src),
            title: tab.title,
            type: tab.type ?? "image",
            alt: tab.alt,
          })),
          caption: (
            <Typography variant="subtitle2" mb={4}>
              {typedPageData.topTabs.caption}
            </Typography>
          ),
        }}
        sections={
          <>
            <CardsSection
              description={
                primarySection?.description ? (
                  <Typography maxWidth={600} variant="body1" pb={2}>
                    {primarySection.description}
                  </Typography>
                ) : undefined
              }
              cards={ITSCards1}
            />
            <TabsSection
              id="intelligent-ts-tcg"
              headline={typedPageData.tabsSection?.headline}
              details={typedPageData.tabsSection?.details}
              contentList={ITSTabs}
              contentSize={{
                height: { sm: "auto", md: "auto", lg: "auto" },
                width: { sm: "316px", md: "100%", lg: "516px" },
              }}
              caption={typedPageData.tabsSection?.caption}
              button={false}
              isPanelCard={true}
            />

            {typedPageData.accordionSection && typedPageData.accordionSection.items.length > 0 ? (
              <AccordionSection
                overline={typedPageData.accordionSection.overline}
                title={typedPageData.accordionSection.title}
                description={typedPageData.accordionSection.description}
                items={typedPageData.accordionSection.items}
              />
            ) : null}

            {typedPageData.textGroups?.[0] && (
              <TextGroup
                id="intelligent-ts-tg1"
                title={typedPageData.textGroups[0].title}
                subtitle={typedPageData.textGroups[0].subtitle}
                bodies={typedPageData.textGroups[0].bodies}
                bullets={typedPageData.textGroups[0].bullets}
              />
            )}

            {benefitsSection && ITSCards2.length > 0 ? (
              <CardsSection
                title={benefitsSection.title}
                cards={ITSCards2}
                closingText={benefitsSection.closingText}
              />
            ) : null}
          </>
        }
        bottomButton={typedPageData.bottomButton}
      />
    </Box>
  );
}

